import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Receipt,
  Truck,
  ScrollText,
  Users,
  Package,
  PiggyBank,
  BookOpenText,
  BookOpen,
  Settings as SettingsIcon,
  LogOut,
  Moon,
  Sun,
  ShoppingCart,
  ChevronRight,
  Factory,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { useAuth } from "@/auth";
import type { FeatureName } from "@/lib/features";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { APP_LOGO, APP_NAME, APP_TAGLINE } from "@/lib/brand";
import { ServerStatusIndicator } from "@/components/ServerStatusIndicator";
import { IdleTimeoutDialog } from "@/components/IdleTimeoutDialog";
import {
  IDLE_TIMEOUT_MS,
  WARNING_BEFORE_MS,
  useIdleTimeout,
} from "@/hooks/useIdleTimeout";

type IconType = typeof LayoutDashboard;

// A single navigable destination in the sidebar.
interface NavLeaf {
  kind: "leaf";
  to: string;
  label: string;
  icon: IconType;
  end?: boolean;
  // Module key matched against canAccess(). Omitted entries are always shown
  // (e.g. Settings, which every signed-in user can reach for self-service).
  // Module-gated entries — including the Dashboard — respect canAccess().
  module?: string;
  // Product-level feature flag. When set and the flag is OFF, the leaf (and a
  // group whose only children are hidden) disappears regardless of permission.
  feature?: FeatureName;
}

// A collapsible parent that purely groups child leaves visually. The parent
// has no route of its own — clicking it toggles the submenu. Visibility
// follows the children: if every child is hidden, the whole group disappears.
interface NavGroup {
  kind: "group";
  key: string;
  label: string;
  icon: IconType;
  children: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

const nav: NavEntry[] = [
  {
    kind: "leaf",
    to: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
    end: true,
    module: "dashboard",
  },
  {
    kind: "group",
    key: "sales",
    label: "Sales",
    icon: ShoppingCart,
    children: [
      {
        kind: "leaf",
        to: "/customers",
        label: "Entity",
        icon: Users,
        module: "customers",
      },
      {
        kind: "leaf",
        to: "/quotations",
        label: "Quotations",
        icon: FileText,
        module: "quotations",
      },
      {
        kind: "leaf",
        to: "/waybills",
        label: "Waybills",
        icon: Truck,
        module: "waybills",
      },
      {
        kind: "leaf",
        to: "/sales-invoices",
        label: "Sales Invoices",
        icon: ScrollText,
        module: "sales_invoices",
      },
    ],
  },
  {
    kind: "group",
    key: "inventory",
    label: "Inventory",
    icon: Package,
    children: [
      {
        kind: "leaf",
        to: "/products",
        label: "Products",
        icon: Package,
        module: "products",
      },
      {
        kind: "leaf",
        to: "/inventory/attributes",
        label: "Attributes",
        icon: Package,
        module: "inventory_attributes",
      },
    ],
  },
  // Finance bundles the cash-in & accounting surface: the Finance dashboard,
  // Receipts (cash-in activity), and the General Ledger. GL keeps its own
  // internal tab navigation (Overview / Accounts / Journals / Reports / …)
  // inside the page, so we point the sidebar at /general-ledger as a single
  // leaf instead of duplicating the GL sub-pages here.
  {
    kind: "group",
    key: "production",
    label: "Production",
    icon: Factory,
    children: [
      {
        kind: "leaf",
        to: "/production-orders",
        label: "Production Orders",
        icon: Factory,
        module: "production_orders",
        feature: "production_orders",
      },
    ],
  },
  {
    kind: "group",
    key: "finance",
    label: "Finance",
    icon: PiggyBank,
    children: [
      {
        kind: "leaf",
        to: "/finance",
        label: "Overview",
        icon: LayoutDashboard,
        end: true,
        module: "finance",
      },
      {
        kind: "leaf",
        to: "/receipts",
        label: "Receipts",
        icon: Receipt,
        module: "receipts",
      },
      {
        kind: "leaf",
        to: "/finance/customer-ledger",
        label: "Customer Ledger",
        icon: BookOpen,
        module: "customer_ledger",
      },
      {
        kind: "leaf",
        to: "/general-ledger",
        label: "General Ledger",
        icon: BookOpenText,
        module: "general_ledger",
      },
    ],
  },
  // Settings is intentionally not module-gated: every signed-in user must be
  // able to reach it for the self-service "My Account" tab (change password).
  // Per-tab visibility inside the page is gated by the administrator role.
  { kind: "leaf", to: "/settings", label: "Settings", icon: SettingsIcon },
];

function isLeafVisible(
  leaf: NavLeaf,
  canAccess: (m: string) => boolean,
  isFeatureEnabled: (f: FeatureName) => boolean,
): boolean {
  // A product-level feature flag overrides everything: OFF means hidden even
  // for users who hold the module permission.
  if (leaf.feature && !isFeatureEnabled(leaf.feature)) return false;
  if (!leaf.module) return true;
  return canAccess(leaf.module);
}

// Compute the path a user should land on when a hard redirect to "/" would be
// wrong (e.g. the Dashboard is denied) — the first module-gated nav leaf they
// can actually reach, walked in the exact sidebar order so the fallback feels
// natural. Always-on leaves without a `module` (Settings) are intentionally
// skipped: a user whose ONLY reachable entry is Settings should see the
// friendly no-access page, not be silently parked on the Settings screen.
// Returns null when the user can reach no module at all, which callers map to
// the ungated /no-access route. Because dashboard is itself module-gated here,
// a dashboard-denied user never resolves back to "/", so there is no loop.
export function firstAccessibleRoute(
  canAccess: (m: string) => boolean,
  isFeatureEnabled: (f: FeatureName) => boolean,
): string | null {
  for (const entry of nav) {
    const leaves = entry.kind === "leaf" ? [entry] : entry.children;
    for (const leaf of leaves) {
      if (!leaf.module) continue;
      if (isLeafVisible(leaf, canAccess, isFeatureEnabled)) return leaf.to;
    }
  }
  return null;
}

function filterNav(
  entries: NavEntry[],
  canAccess: (m: string) => boolean,
  isFeatureEnabled: (f: FeatureName) => boolean,
): NavEntry[] {
  const out: NavEntry[] = [];
  for (const entry of entries) {
    if (entry.kind === "leaf") {
      if (isLeafVisible(entry, canAccess, isFeatureEnabled)) out.push(entry);
      continue;
    }
    const visibleChildren = entry.children.filter((c) =>
      isLeafVisible(c, canAccess, isFeatureEnabled),
    );
    if (visibleChildren.length === 0) continue;
    out.push({ ...entry, children: visibleChildren });
  }
  return out;
}

// A child route is considered "matched" when the current pathname equals it
// exactly or is a nested sub-path (e.g. /quotations/new under /quotations).
// We deliberately don't use plain startsWith — that would incorrectly match
// "/quotations" against a hypothetical "/quotations-foo".
function pathMatches(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}

export default function AppShell({ children }: { children: ReactNode }) {
  const { user, logout, canAccess, isFeatureEnabled } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const loc = useLocation();

  const visibleNav = useMemo(
    () => filterNav(nav, canAccess, isFeatureEnabled),
    [canAccess, isFeatureEnabled],
  );

  // Flattened list of every reachable leaf — used to derive the page title
  // shown in the top header.
  const allLeaves = useMemo<NavLeaf[]>(() => {
    const out: NavLeaf[] = [];
    for (const entry of visibleNav) {
      if (entry.kind === "leaf") out.push(entry);
      else out.push(...entry.children);
    }
    return out;
  }, [visibleNav]);

  const current =
    allLeaves.find((n) =>
      n.end ? loc.pathname === n.to : pathMatches(loc.pathname, n.to),
    )?.label ?? "Dashboard";

  // Set of group keys whose children include the active route. Used to
  // highlight the parent and to auto-expand the submenu on deep-link.
  const activeGroupKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const entry of visibleNav) {
      if (entry.kind !== "group") continue;
      const hit = entry.children.some((c) => pathMatches(loc.pathname, c.to));
      if (hit) keys.add(entry.key);
    }
    return keys;
  }, [visibleNav, loc.pathname]);

  // Track expanded/collapsed state per group. We seed it lazily from whatever
  // group owns the current route so a fresh page load on /quotations opens
  // Sales by default.
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(activeGroupKeys),
  );

  // Whenever the user navigates into a group's child (back/forward, link
  // click, etc.) make sure that group is visible. We only ever open groups
  // here — explicit collapses by the user are preserved otherwise.
  useEffect(() => {
    setOpenGroups((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const key of activeGroupKeys) {
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [activeGroupKeys]);

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const initial = (user?.name || user?.username || user?.email || "?")
    .trim()
    .charAt(0)
    .toUpperCase();
  const primaryRole = user?.roles?.[0];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // ---- Idle-timeout session expiry ---------------------------------------
  // Show a warning modal 1 minute before forced logout, then sign the user
  // out if they don't acknowledge it. The hook is gated on `user` so it
  // never runs on /login or while the initial auth refresh is pending —
  // AppShell itself is only mounted under <Private/> which already requires
  // a user, but we belt-and-brace it here so any future change to that
  // contract can't accidentally start the timer on a logged-out shell.
  const [showIdleWarning, setShowIdleWarning] = useState(false);

  const handleIdleWarning = useCallback(() => {
    setShowIdleWarning(true);
  }, []);

  const handleIdleTimeout = useCallback(() => {
    setShowIdleWarning(false);
    logout();
    navigate("/login", { replace: true });
    toast.message("Logged out due to inactivity");
  }, [logout, navigate]);

  const { reset: resetIdleTimer } = useIdleTimeout({
    idleMs: IDLE_TIMEOUT_MS,
    warningMs: WARNING_BEFORE_MS,
    enabled: !!user,
    onWarning: handleIdleWarning,
    onTimeout: handleIdleTimeout,
  });

  const handleIdleAcknowledge = useCallback(() => {
    setShowIdleWarning(false);
    resetIdleTimer();
  }, [resetIdleTimer]);
  // ------------------------------------------------------------------------

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden md:flex w-64 flex-col border-r border-sidebar-border bg-sidebar">
        <div className="flex items-center gap-2 px-5 py-5 border-b border-sidebar-border">
          <img
            src={APP_LOGO}
            alt={APP_NAME}
            className="h-9 w-auto object-contain shrink-0"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
          <div>
            <div className="text-sm font-semibold leading-tight">
              {APP_NAME}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {APP_TAGLINE}
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleNav.map((entry) => {
            if (entry.kind === "leaf") {
              return (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.end}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                      isActive
                        ? "bg-gradient-primary text-primary-foreground shadow-card"
                        : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                    )
                  }
                >
                  <entry.icon className="h-4 w-4" />
                  {entry.label}
                </NavLink>
              );
            }

            const isOpen = openGroups.has(entry.key);
            const isActive = activeGroupKeys.has(entry.key);
            const groupId = `nav-group-${entry.key}`;

            return (
              <div key={entry.key}>
                <button
                  type="button"
                  onClick={() => toggleGroup(entry.key)}
                  aria-expanded={isOpen}
                  aria-controls={groupId}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                  )}
                >
                  <entry.icon className="h-4 w-4" />
                  <span className="flex-1 text-left">{entry.label}</span>
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                      isOpen && "rotate-90",
                    )}
                  />
                </button>
                {isOpen && (
                  <div
                    id={groupId}
                    role="group"
                    aria-label={entry.label}
                    className="mt-1 ml-4 space-y-1 border-l border-sidebar-border pl-2"
                  >
                    {entry.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.end}
                        className={({ isActive: childActive }) =>
                          cn(
                            "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all",
                            childActive
                              ? "bg-gradient-primary text-primary-foreground shadow-card"
                              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                          )
                        }
                      >
                        <child.icon className="h-4 w-4" />
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-sidebar-border space-y-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={toggle}
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </Button>
          <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-sidebar-accent">
            <div className="h-9 w-9 rounded-full bg-gradient-primary flex items-center justify-center text-white text-sm font-semibold">
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {user?.name || user?.username || user?.email}
              </div>
              <div className="text-[11px] text-muted-foreground capitalize truncate">
                {primaryRole ?? "user"}
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleLogout}
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="flex items-center justify-between gap-3 px-4 md:px-8 py-3 border-b bg-background/95 sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <h1 className="font-semibold text-base md:text-lg truncate">
            {current}
          </h1>
          <div className="flex items-center gap-2 shrink-0">
            <ServerStatusIndicator />
            <Button
              size="icon"
              variant="ghost"
              className="md:hidden"
              onClick={toggle}
              title="Toggle theme"
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="md:hidden"
              onClick={handleLogout}
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <div className="flex-1 p-4 md:px-8 md:py-5 animate-fade-in">{children}</div>
      </main>

      <IdleTimeoutDialog
        open={showIdleWarning}
        warningMs={WARNING_BEFORE_MS}
        onAcknowledge={handleIdleAcknowledge}
      />
    </div>
  );
}
